import { median } from "mathjs";

const percentileThresholds = (items: number[], numberOfGroups: number) => {
    items.sort((a, b) => a - b);
    const thresholds = [];
    const numberOfItems = items.length;
    const step = 100 / numberOfGroups;
    let percentile = 0;
    for (let i = 0; i < numberOfGroups; i++) {
        if (numberOfItems === 0) {
            thresholds.push(0);
            continue;
        }
        const rank = (percentile / 100) * (numberOfItems - 1);
        const intgr = Math.floor(rank);
        const fract = rank % 1;

        const threshold = items[intgr] + (fract * (items[intgr + 1] - items[intgr]));
        thresholds.push(threshold);

        percentile += step;
    }
    return thresholds;
};

const customPercentileThresholds = (items: number[], percentiles: number[]) => {
    items.sort((a, b) => a - b);
    const thresholds = [];
    const numberOfItems = items.length;
    for (let i = 0; i < percentiles.length; i++) {
        if (numberOfItems === 0) {
            thresholds.push(0);
            continue;
        }
        const rank = (percentiles[i]) * (numberOfItems - 1);
        const intgr = Math.floor(rank);
        const fract = rank % 1;

        const threshold = items[intgr] + (fract * (items[intgr + 1] - items[intgr]));
        thresholds.push(threshold);
    }
    return thresholds;
};

const round = (number: number, decimalPlaces: number) => {
    const isNegative = number < 0;
    if (isNegative) number *= -1;
    const round = Number(Math.round(parseFloat(number + "e" + decimalPlaces)) + "e-" + decimalPlaces);
    if (isNegative) return round * -1;
    return round;
};

const safePercentageChange = (newVal: number, oldVal: number): number => {
    if (oldVal === 0) {
        if (newVal > 0) {
            return 100;
        } else {
            return 0;
        }
    }

    return 100 * ((newVal - oldVal) / oldVal);
};

const safePercentage = (numerator: number, denominator: number): number => {
    if (denominator === 0) {
        if (numerator > 0) {
            return 100;
        } else {
            return 0;
        }
    }

    return 100 * (numerator / denominator);
};

const haversineDistance = (
    latitudeA: number,
    longitudeA: number,
    latitudeB: number,
    longitudeB: number
): number => {
    const toRadians = (deg: number) => deg * (Math.PI / 180);
    const earthRadius = 6371; // Earth radius in kilometers

    const lat1 = toRadians(latitudeA);
    const lon1 = toRadians(longitudeA);
    const lat2 = toRadians(latitudeB);
    const lon2 = toRadians(longitudeB);

    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadius * c;
};

const safeMedian = (values: number[]): number | undefined => {
    const filteredValues = values.filter(value => value !== null || value !== undefined);
    if (filteredValues.length === 0) {
        return undefined;
    }
    return median(filteredValues);
};

const centilesStandardisedMedian = (centiles: number[]) => {
    if (centiles.length === 0) {
        return 0;
    }
    const centilesMedian = median(centiles);
    let standardisedMedian = 45;
    let difference = 100; // initialise with a number larger than any potential difference
    for (let possibleCentileMedian = 5; possibleCentileMedian <= 95; possibleCentileMedian += 10) {
        const absDiff = Math.abs(centilesMedian - possibleCentileMedian);
        if (absDiff <= difference) {
            difference = absDiff;
            standardisedMedian = possibleCentileMedian;
        }
    }
    return standardisedMedian;
};

const pearsonCorrelationPopulation = (a: number[], b: number[]) => {
    if (a.length !== b.length || a.length === 0 || b.length === 0) {
        return null;
    }

    const meanA = a.reduce((sum, val) => sum + val, 0) / a.length;
    const meanB = b.reduce((sum, val) => sum + val, 0) / b.length;

    let numerator = 0;
    let denominatorA = 0;
    let denominatorB = 0;

    for (let i = 0; i < a.length; i++) {
        const deviationFromMeanOfA = a[i] - meanA;
        const deviationFromMeanOfB = b[i] - meanB;

        numerator += deviationFromMeanOfA * deviationFromMeanOfB;
        denominatorA += deviationFromMeanOfA * deviationFromMeanOfA;
        denominatorB += deviationFromMeanOfB * deviationFromMeanOfB;
    }

    const denominator = Math.sqrt(denominatorA * denominatorB);

    if (denominator === 0) {
        return null;
    }

    return numerator / denominator;
};

const mathUtils = {
    percentileThresholds,
    customPercentileThresholds,
    round,
    safePercentageChange,
    safePercentage,
    haversineDistance,
    safeMedian,
    centilesStandardisedMedian,
    pearsonCorrelationPopulation
};

export default mathUtils;
